/**
 * Utilitário para traduzir mensagens de erro da API de campanhas para português
 */

interface ApiError {
  status?: number;
  data?: {
    message?: string;
    messages?: string[];
    statusCode?: number;
  };
}

/**
 * Mapeamento de mensagens de erro em inglês para português
 */
const errorTranslations: Record<string, string | ((match: RegExpMatchArray) => string)> = {
  // ===== Erros de validação de produtos =====
  'Some product codes do not exist in ITE001 database':
    'Alguns códigos de produtos não foram encontrados no cadastro de itens (ITE001). Verifique se os SKUs informados estão corretos.',

  // Regex para capturar números nas mensagens de produtos
  'Some product codes do not exist in ITE001 database. Expected: (\\d+), Found: (\\d+)':
    (match: RegExpMatchArray) =>
      `Alguns códigos de produtos não foram encontrados no cadastro de itens. Esperados: ${match[1]}, Encontrados: ${match[2]}. Verifique se os SKUs informados estão corretos.`,

  // Mensagem em português do backend
  'Alguns códigos de produtos não existem na base ITE001. Esperado: (\\d+), Encontrado: (\\d+)':
    (match: RegExpMatchArray) =>
      `Alguns códigos de produtos não foram encontrados no cadastro de itens. Esperados: ${match[1]}, Encontrados: ${match[2]}. Verifique se os SKUs informados estão corretos.`,

  // ===== Erros de conflito de campanhas (vigência) =====
  'Campaign conflict detected':
    'Conflito de campanha detectado. Existem produtos que já participam de outras campanhas no mesmo período.',
  'Items already in active campaigns':
    'Alguns itens já estão em campanhas ativas no mesmo período. Um produto não pode participar de múltiplas campanhas simultaneamente.',
  'Product conflict detected':
    'Conflito de produto detectado. Este item já participa de outra campanha no mesmo período.',
  'Conflict validation failed':
    'Validação de conflitos falhou. Verifique se os produtos não estão em outras campanhas ativas.',
  'There are products conflicting with other active campaigns.':
    'Existem produtos em conflito com outras campanhas ativas no mesmo período. Verifique os itens e tente novamente.',
  'Existem produtos em conflito com outras campanhas ativas.':
    'Existem produtos em conflito com outras campanhas ativas no mesmo período. Verifique os itens antes de prosseguir.',

  // ===== Erros de prorrogação (Story 1.1) =====
  'campaign.extend.date.must.be.future':
    'A nova data final deve ser maior que a data atual.',
  'campaign.extend.date.must.be.greater':
    'A nova data final deve ser maior que a data final atual da campanha.',
  'campaign.expired.cannot.extend':
    'Campanhas expiradas não podem ser prorrogadas.',
  'campaign.extend.noop':
    'A nova data é igual à data atual — nenhuma alteração foi necessária.',
  'campaign.extend.success':
    'Campanha prorrogada com sucesso.',

  // ===== Erros de datas =====
  'End date must be greater than or equal to start date':
    'A data de término deve ser maior ou igual à data de início.',
  'End date must be after start date':
    'A data de término deve ser posterior à data de início.',
  'Data de término deve ser maior ou igual à data de início':
    'A data de término deve ser maior ou igual à data de início.',
  'Data de término deve ser posterior à data de início':
    'A data de término deve ser posterior à data de início.',
  'Start date is required':
    'A data de início é obrigatória.',
  'End date is required':
    'A data de término é obrigatória.',
  'Data de início inválida':
    'A data de início informada é inválida.',
  'Data de término inválida':
    'A data de término informada é inválida.',

  // ===== Erros de descrição =====
  'Description is required':
    'A descrição da campanha é obrigatória.',
  'Description must have at least 3 characters':
    'A descrição deve ter no mínimo 3 caracteres.',
  'Descrição deve ter no mínimo 3 caracteres':
    'A descrição deve ter no mínimo 3 caracteres.',
  'Descrição deve ter no máximo 100 caracteres':
    'A descrição deve ter no máximo 100 caracteres.',

  // ===== Erros de lojas =====
  'At least one store is required':
    'Selecione pelo menos uma loja.',
  'Store not found':
    'Loja não encontrada.',
  'Specify storeCode=999 for all stores or provide stores array':
    'Especifique todas as lojas ou selecione lojas específicas.',
  'Especifique storeCode=999 para todas as lojas ou forneça array de lojas':
    'Especifique todas as lojas ou selecione lojas específicas.',
  'Stores must be specified when storeIndicator = 1':
    'As lojas devem ser especificadas quando não é para todas as lojas.',
  'Lojas devem ser especificadas quando storeIndicator = 1':
    'As lojas devem ser especificadas quando não é para todas as lojas.',

  // ===== Erros de quantidade (Leve X Pague Y) =====
  'Take quantity must be greater than pay quantity':
    'A quantidade "Leve" deve ser maior que a quantidade "Pague".',
  'Take quantity must be at least 1':
    'A quantidade "Leve" deve ser no mínimo 1.',

  // ===== Erros de desconto =====
  'Discount value cannot be negative':
    'O valor do desconto não pode ser negativo.',
  'Invalid discount type':
    'Tipo de desconto inválido.',

  // ===== Erros de listas (PRO017 - Combo Moderno) =====
  'At least 2 lists are required':
    'A campanha deve ter no mínimo 2 listas.',
  'Each list must have at least 2 items':
    'Cada lista deve ter no mínimo 2 produtos.',
  'Maximum 10 lists allowed':
    'A campanha pode ter no máximo 10 listas.',
  'Campaign must have at least 2 lists':
    'A campanha deve ter no mínimo 2 listas.',
  'Campaign must have at most 10 lists':
    'A campanha pode ter no máximo 10 listas.',
  'Campanha deve ter no mínimo 2 listas':
    'A campanha deve ter no mínimo 2 listas.',
  'Campanha deve ter no máximo 10 listas':
    'A campanha pode ter no máximo 10 listas.',

  // Regex para lista com número dinâmico
  'List (\\d+) must have at least 2 items':
    (match: RegExpMatchArray) =>
      `A Lista ${match[1]} deve ter no mínimo 2 produtos.`,
  'Lista (\\d+) deve ter no mínimo 2 itens':
    (match: RegExpMatchArray) =>
      `A Lista ${match[1]} deve ter no mínimo 2 produtos.`,

  // Erros de numeração de listas
  'List numbers must be sequential starting from 1. Expected: (\\d+), Got: (\\d+)':
    (match: RegExpMatchArray) =>
      `Os números das listas devem ser sequenciais começando em 1. Esperado: ${match[1]}, Recebido: ${match[2]}.`,
  'Números de lista devem ser sequenciais começando em 1. Esperado: (\\d+), Recebido: (\\d+)':
    (match: RegExpMatchArray) =>
      `Os números das listas devem ser sequenciais começando em 1. Esperado: ${match[1]}, Recebido: ${match[2]}.`,

  // ===== Erros de campanhas não encontradas =====
  'Campanha PRO013 #(\\d+) não encontrada':
    (match: RegExpMatchArray) =>
      `A campanha PRO013 #${match[1]} não foi encontrada.`,
  'Campanha PRO014 #(\\d+) não encontrada':
    (match: RegExpMatchArray) =>
      `A campanha PRO014 #${match[1]} não foi encontrada.`,
  'Campanha PRO017 #(\\d+) não encontrada':
    (match: RegExpMatchArray) =>
      `A campanha PRO017 #${match[1]} não foi encontrada.`,
  'Campanha PRO014 (\\d+) não encontrada':
    (match: RegExpMatchArray) =>
      `A campanha PRO014 #${match[1]} não foi encontrada.`,
  'Campaign not found':
    'Campanha não encontrada.',

  // ===== Erros de edição de campanhas =====
  'Campanhas expiradas não podem ser editadas':
    'Campanhas expiradas não podem ser editadas. Crie uma nova campanha.',
  'Expired campaigns cannot be edited':
    'Campanhas expiradas não podem ser editadas. Crie uma nova campanha.',
  'Não é possível reduzir a data de término de uma campanha ativa':
    'Não é possível reduzir a data de término de uma campanha ativa. Apenas estender é permitido.',
  'Cannot reduce end date of an active campaign':
    'Não é possível reduzir a data de término de uma campanha ativa. Apenas estender é permitido.',

  // Regex para campos imutáveis
  'Campos (.+) não podem ser alterados em campanhas ativas':
    (match: RegExpMatchArray) =>
      `Os campos ${match[1]} não podem ser alterados em campanhas ativas.`,
  'Fields (.+) cannot be changed in active campaigns':
    (match: RegExpMatchArray) =>
      `Os campos ${match[1]} não podem ser alterados em campanhas ativas.`,

  // ===== Erros de produtos em campanhas =====
  'Deve adicionar no mínimo 1 produto':
    'É necessário adicionar no mínimo 1 produto.',
  'Códigos de produtos devem ser strings':
    'Os códigos de produtos devem ser textos válidos.',
  'Produtos duplicados não são permitidos':
    'Não é permitido adicionar produtos duplicados.',
  'É necessário selecionar ao menos 1 produto':
    'Selecione pelo menos 1 produto para continuar.',
  'Limite de 10.000 produtos por operação':
    'Limite máximo de 10.000 produtos por operação.',

  // Regex para produto não encontrado
  'Produto (.+) não encontrado na campanha (\\d+)':
    (match: RegExpMatchArray) =>
      `O produto ${match[1]} não foi encontrado na campanha #${match[2]}.`,

  // ===== Erros de mínimo de produtos =====
  'Operação bloqueada: a campanha deve ter no mínimo 2 produtos ativos':
    'Operação bloqueada: a campanha deve manter no mínimo 2 produtos ativos.',

  // ===== Erros de upload de arquivo =====
  'Nenhum arquivo foi enviado':
    'Nenhum arquivo foi enviado. Selecione um arquivo Excel (.xlsx) para continuar.',
  'No file was uploaded':
    'Nenhum arquivo foi enviado. Selecione um arquivo Excel (.xlsx) para continuar.',
  'Formato de arquivo inválido. Envie um arquivo Excel (.xlsx)':
    'Formato de arquivo inválido. Por favor, envie um arquivo Excel (.xlsx).',
  'Invalid file format. Please upload an Excel file (.xlsx)':
    'Formato de arquivo inválido. Por favor, envie um arquivo Excel (.xlsx).',
  'Planilha não encontrada no arquivo Excel':
    'Planilha não encontrada no arquivo Excel. Verifique se o arquivo está correto.',
  'Nenhum SKU válido encontrado no arquivo. Certifique-se de preencher a coluna SKU.':
    'Nenhum SKU válido encontrado no arquivo. Certifique-se de preencher a coluna SKU corretamente.',

  // ===== Erros de cancelamento =====
  'Cancellation reason is required':
    'O motivo do cancelamento é obrigatório.',
  'Reason must not exceed 500 characters':
    'O motivo do cancelamento deve ter no máximo 500 caracteres.',

  // ===== Erros de propagação =====
  'Propagation record not found for (.+) #(\\d+)':
    (match: RegExpMatchArray) =>
      `Registro de propagação não encontrado para ${match[1]} #${match[2]}.`,
  'Registro de propagação não encontrado para (.+) #(\\d+)':
    (match: RegExpMatchArray) =>
      `Registro de propagação não encontrado para ${match[1]} #${match[2]}.`,
  'Store record #(\\d+) not found':
    (match: RegExpMatchArray) =>
      `Registro da loja #${match[1]} não encontrado.`,

  // ===== Erros genéricos =====
  'Internal server error':
    'Erro interno do servidor. Tente novamente mais tarde.',
  'An unexpected error occurred':
    'Ocorreu um erro inesperado. Verifique sua conexão e tente novamente. Se o problema persistir, entre em contato com o suporte.',
  'An unexpected error occurred.':
    'Ocorreu um erro inesperado. Verifique sua conexão e tente novamente. Se o problema persistir, entre em contato com o suporte.',
  'Unauthorized':
    'Sessão expirada. Faça login novamente.',
  'Forbidden':
    'Você não tem permissão para realizar esta ação.',
  'Not found':
    'Recurso não encontrado.',
  'Bad request':
    'Requisição inválida. Verifique os dados informados.',
  'Network Error':
    'Erro de conexão. Verifique sua internet e tente novamente.',
  'Request timeout':
    'A requisição demorou muito para responder. Tente novamente.',
  'Service unavailable':
    'Serviço temporariamente indisponível. Tente novamente em alguns minutos.',
};

/**
 * Traduz uma mensagem de erro da API para português
 */
export function translateApiError(message: string): string {
  // Primeiro, tenta match exato
  if (errorTranslations[message] && typeof errorTranslations[message] === 'string') {
    return errorTranslations[message] as string;
  }

  // Depois, tenta match com regex para mensagens com valores dinâmicos
  for (const [pattern, translation] of Object.entries(errorTranslations)) {
    if (typeof translation === 'function') {
      const regex = new RegExp(pattern);
      const match = message.match(regex);
      if (match) {
        return translation(match);
      }
    }
  }

  // Se não encontrar tradução, retorna a mensagem original
  return message;
}

/**
 * Extrai e traduz mensagens de erro de um objeto de erro da API
 */
export function getTranslatedErrorMessage(error: unknown, defaultMessage: string): string {
  const apiError = error as ApiError;

  // Tenta extrair mensagem do campo messages (array)
  if (apiError?.data?.messages && Array.isArray(apiError.data.messages) && apiError.data.messages.length > 0) {
    const translatedMessages = apiError.data.messages.map(translateApiError);
    return translatedMessages.join(' ');
  }

  // Tenta extrair mensagem do campo message
  if (apiError?.data?.message) {
    return translateApiError(apiError.data.message);
  }

  // Retorna mensagem padrão
  return defaultMessage;
}

/**
 * Verifica se o erro é um conflito de campanha (409)
 */
export function isCampaignConflictError(error: ApiError): boolean {
  return error?.status === 409 && !!error?.data;
}
